import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of, OperatorFunction } from 'rxjs';
import * as SearchBoardActions from '@store/search-board/search-board.actions';
import {
  DownloadCustomizedSearchBoardReport,
  DownloadSearchBoardCards,
  DownloadSearchBoardMap,
  DownloadSearchBoardMapTable,
  DownloadSearchBoardReportFailure,
  DownloadSearchBoardReports,
  DownloadSearchBoardReportSuccess,
  DownloadSearchBoardTable,
  EmailCustomizedSearchBoardReport,
  OpenActionBarModal,
  SendEmail,
  StartActionBarAction
} from '@store/search-board/search-board.actions';
import { bufferCount, catchError, filter, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { RibbonService } from '@shared/services/ribbon.service';
import { RIBBON_TYPE } from '@shared/models/ribbon.model';
import {
  ActionModalData,
  ActionModalResult,
  ActionModalTypes,
  DownloadParams,
  Email,
  EmailDownloadPreferences,
  FileLink,
  PropertyIdentifier
} from '@store/search-board/search-board.model';
import { SpinnerOverlayService } from '@shared/services/spinner-overlay.service';
import { HtmlToCanvasService } from '@shared/services/html-to-canvas.service';
import { SearchDownloadService } from '@root/search/services/search-download.service';
import { ActionName } from '@shared/models/effects-names.model';
import { ReportUtils } from '@shared/utils/report-utils';
import { select, Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import { DataElement, PreferenceCode } from '@store/user/user.model';
import {
  selectCardsAttributes,
  selectEmailDownloadPreferences,
  selectGridHeaders,
  selectIsMobile,
  selectPreferenceValue,
  selectUserName
} from '@store/user/user.selector';
import { EmailService } from '@root/search/services/email.service';
import { ActionBarModalComponent } from '@shared/components/action-bar/action-bar-modal/action-bar-modal.component';
import { ModalType } from '@shared/models/modal.model';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { OutputFlags, ReportType } from '@store/reports/reports.model';
import { PremiumFieldCodes } from '@root/search/models/display-fields.constant';
import { selectIsPremiumViewAllowed } from '@store/search-board/search-board.selector';

@Injectable()
export class SearchBoardActionBarEffects {
  constructor(private readonly actions$: Actions,
              private readonly store$: Store<AppState>,
              private readonly dialog: MatDialog,
              private readonly spinnerOverlayService: SpinnerOverlayService,
              private readonly htmlToCanvasService: HtmlToCanvasService,
              private readonly searchDownloadService: SearchDownloadService,
              private readonly emailService: EmailService,
              private readonly ribbonService: RibbonService) {
  }

  openActionBarModal$: Observable<StartActionBarAction> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType<OpenActionBarModal>(SearchBoardActions.OPEN_ACTION_BAR_MODAL),
        withLatestFrom(
          this.store$.pipe(select(selectIsMobile)),
          this.store$.pipe(
            select(selectUserName),
            map((userName: string) => `The report was e-mailed to you from ${userName}`)
          ),
          this.store$.pipe(select(selectEmailDownloadPreferences)),
          this.store$.pipe(select(selectPreferenceValue(PreferenceCode.PG_RL_GENERAL_PREFERENCES, PreferenceCode.PG_GENPREF_USER_EMAIL)))
        ),
        switchMap(([action, isMobile, messageBody, emailDownloadPreferences, replyTo]:
                     [OpenActionBarModal, boolean, string, EmailDownloadPreferences, string]) => {
          return this.dialog.open<ActionBarModalComponent, ActionModalData>(ActionBarModalComponent, {
            panelClass: ModalType.SMALL,
            data: {
              modalType: action.payload.modalType,
              propertyIdentifiers: action.payload.propertyIdentifiers,
              emailDownloadPreferences: emailDownloadPreferences,
              messageBody: messageBody,
              replyTo,
              isMobile: isMobile,
              reportsWithMapDisabled: action.payload.reportsWithMapDisabled
            }
          })
            .afterClosed()
            .pipe(
              filter(Boolean),
              map((data: ActionModalResult) => new StartActionBarAction(data))
            );
        })
      );
  });

  startActionBar$ = createEffect(() => {
    return this.actions$
      .pipe(
        ofType<StartActionBarAction>(SearchBoardActions.START_ACTION_BAR_ACTION),
        withLatestFrom(
          this.store$.pipe(select(selectCardsAttributes)),
          this.store$.pipe(select(selectGridHeaders)),
          this.store$.pipe(select(selectIsPremiumViewAllowed))
        ),
        switchMap(([action, cardsAttributes, gridHeaders, isPremiumViewAllowed]:
                     [StartActionBarAction, Array<DataElement>, Array<DataElement>, boolean]) => {
          const parsedGrid = gridHeaders.filter((header) => {
            return !(!isPremiumViewAllowed && !!PremiumFieldCodes.find((i) => i === header.fieldCode));
          });
          const propertyIdentifiers: Array<PropertyIdentifier> = action.payload.propertyIdentifiers;
          const reportTypes: Array<ReportType> = action.payload.reportTypes;
          const modalType: ActionModalTypes = action.payload.modalType;

          const downloadActions = reportTypes.map((reportType: ReportType) => {
            switch (reportType) {
              case ReportType.MAP_IMAGE:
                return new DownloadSearchBoardMap();
              case ReportType.TABLE:
                return new DownloadSearchBoardTable({propertyIdentifiers, dataElements: parsedGrid});
              case ReportType.CARD:
                return new DownloadSearchBoardCards({propertyIdentifiers, dataElements: cardsAttributes});
              case ReportType.MAP_TABLE:
                return new DownloadSearchBoardMapTable({propertyIdentifiers, dataElements: parsedGrid});
              case ReportType.PROPERTY_DETAILS:
                return new DownloadSearchBoardReports(propertyIdentifiers);
            }
          })
            .filter(Boolean);

          return [
            modalType === ActionModalTypes.EMAIL
              ? new EmailCustomizedSearchBoardReport(action.payload)
              : new DownloadCustomizedSearchBoardReport(action.payload),
            ...downloadActions
          ];
        })
      );
  });

  emailCustomizedSearchBoardReport$: Observable<SendEmail> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.EMAIL_CUSTOMIZED_SEARCH_BOARD_REPORT),
        switchMap((action: EmailCustomizedSearchBoardReport) => {
          return this.getReportFiles(action)
            .pipe(
              this.emailReport(action.payload.emailData)
            );
        })
      );
  });

  downloadCustomizedSearchBoardReport$: Observable<FileLink> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.DOWNLOAD_CUSTOMIZED_SEARCH_BOARD_REPORT),
        switchMap((action) => this.getReportFiles(action)),
        this.downloadPdf()
      );
  }, {dispatch: false});

  downloadSearchBoardMap$: Observable<DownloadSearchBoardReportSuccess | DownloadSearchBoardReportFailure> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.DOWNLOAD_SEARCH_BOARD_MAP),
        this.spinnerOverlayService.wrapRequest(() => {
          return this.getMapImage()
            .pipe(
              switchMap((mapImg: string) => this.searchDownloadService.downloadMap(mapImg)),
              this.handleDownload()
            );
        }, ActionName.DIRECT_LINK)
      );
  });

  downloadSearchBoardTable$: Observable<DownloadSearchBoardReportSuccess | DownloadSearchBoardReportFailure> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.DOWNLOAD_SEARCH_BOARD_TABLE),
        this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.DownloadSearchBoardTable) => {
          return this.searchDownloadService.downloadTable(
            action.payload.propertyIdentifiers,
            action.payload.dataElements
          )
            .pipe(
              this.handleDownload()
            );
        }, ActionName.DIRECT_LINK)
      );
  });

  downloadSearchBoardMapTable$: Observable<DownloadSearchBoardReportSuccess | DownloadSearchBoardReportFailure> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.DOWNLOAD_SEARCH_BOARD_MAP_TABLE),
        switchMap((action: DownloadSearchBoardMapTable) => {
          return this.getMapImage()
            .pipe(
              map((mapImg: string) => [mapImg, action.payload])
            );
        }),
        this.spinnerOverlayService.wrapRequest(([mapImg, {propertyIdentifiers, dataElements}]: [string, DownloadParams]) => {
          return this.searchDownloadService.downloadMapTable(mapImg, propertyIdentifiers, dataElements).pipe(
            this.handleDownload()
          );
        }, ActionName.DIRECT_LINK)
      );
  });

  downloadSearchBoardCards$: Observable<DownloadSearchBoardReportSuccess | DownloadSearchBoardReportFailure> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.DOWNLOAD_SEARCH_BOARD_CARDS),
        this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.DownloadSearchBoardCards) => {
          return this.searchDownloadService.downloadCards(
            action.payload.propertyIdentifiers, action.payload.dataElements
          )
            .pipe(
              this.handleDownload()
            );
        }, ActionName.DIRECT_LINK)
      );
  });

  downloadSearchBoardReports$: Observable<DownloadSearchBoardReportSuccess | DownloadSearchBoardReportFailure> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.DOWNLOAD_SEARCH_BOARD_REPORTS),
        this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.DownloadSearchBoardReports) => {
          return this.searchDownloadService.downloadPropertyDetailsReports(action.payload).pipe(
            this.handleDownload()
          );
        }, ActionName.DIRECT_LINK)
      );
  });

  downloadPropertyPhotosReport$: Observable<FileLink> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.DOWNLOAD_PROPERTY_PHOTOS_REPORT),
      this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.DownloadPropertyPhotosReport) => {
        return this.searchDownloadService.downloadPropertyPhotosReport(action.payload, OutputFlags.PRINT).pipe(
          this.downloadPdf()
        );
      }, ActionName.GET_REPORT)
    );
  }, {dispatch: false});

  sendEmail$: Observable<any> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.SEND_EMAIL),
        switchMap((action: SendEmail) => {
          return this.emailService.send(action.payload)
            .pipe(
              catchError(() => of(void (0))),
              tap((emailResult: string) => {
                if (emailResult) {
                  this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, 'Email has been sent.');
                } else {
                  this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Email cannot be sent right now.');
                }
              }),
            );
        })
      );
  }, { dispatch: false });

  private downloadPdf(errorMessage: string = 'PDF cannot be generated. Please try again later.'): OperatorFunction<FileLink, FileLink> {
    return (fileLink$: Observable<FileLink>) => fileLink$.pipe(
      catchError(() => of(void (0))),
      tap((response: FileLink) => {
        if (response) {
          ReportUtils.downloadPdf(response);
        } else {
          this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, errorMessage);
        }
      })
    );
  }

  private handleDownload(): OperatorFunction<FileLink, DownloadSearchBoardReportSuccess | DownloadSearchBoardReportFailure> {
    return (fileLink$: Observable<FileLink>) => fileLink$.pipe(
      map((fileLink: FileLink) => fileLink && fileLink.fileLink
        ? new DownloadSearchBoardReportSuccess(fileLink)
        : new DownloadSearchBoardReportFailure()
      ),
      catchError(() => of(new DownloadSearchBoardReportFailure()))
    );
  }

  private emailReport(emailData: Email): (source: Observable<FileLink>) => Observable<SendEmail> {
    return (source) => source.pipe(
      filter(Boolean),
      map((fileLink: FileLink) => {
        const fileName = ReportUtils.getFileName(fileLink.fileLink);
        const reportType = fileLink.reportType;
        return new SendEmail({...emailData, fileName, reportType});
      })
    );
  }

  private getReportFiles<T extends { payload: ActionModalResult }>(action: T): Observable<FileLink> {
    return this.actions$.pipe(
      ofType(SearchBoardActions.DOWNLOAD_SEARCH_BOARD_REPORT_SUCCESS, SearchBoardActions.DOWNLOAD_SEARCH_BOARD_REPORT_FAILURE),
      bufferCount<DownloadSearchBoardReportSuccess | DownloadSearchBoardReportFailure>(action.payload.reportTypes.length),
      map((downloads: Array<DownloadSearchBoardReportSuccess | DownloadSearchBoardReportFailure>) => {
        return downloads.map((download) => download.payload).filter((fileLink) => !!fileLink);
      }),
      take(1),
      this.spinnerOverlayService.wrapRequest((fileLinks: Array<FileLink>) => {
        if (fileLinks.length <= 1) {
          return of(fileLinks[0]);
        }
        const reports = fileLinks.reduce((reportTypes, link) => {
          reportTypes[link.reportType] = ReportUtils.getFileName(link.fileLink);
          return reportTypes;
        }, {});
        return this.searchDownloadService.downloadCustomizedReport({
          reports,
          propertyData: {}
        }).pipe(catchError(() => of(void (0))));
      }, ActionName.DIRECT_LINK),
    );
  }

  private getMapImage(): Observable<string> {
    return this.htmlToCanvasService.htmlToImage(document.querySelector('#js-map-print'));
  }
}
